import { useMutation } from '@tanstack/react-query';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';
import { clearTokens, refreshTokenForDeviceConfirm } from 'utils/token';
import {
  getInitClientFromSessionStorage,
  getSessionIdFromSessionStorage,
  getTraceIdFromSessionStorage,
  shouldPersistAuthData,
} from 'utils/url';
import { OIDCAuthenticationClient } from '../httpClients/OIDCAuthenticationClient';
import { MUTATION_KEYS } from './consts';

type AdoptDeviceCodeVariables = {
  userCode: string;
};

/**
 * ST-landing leg of a CLI login: the user typed the device code shown by their terminal.
 * The ST has no device session yet (the CLI polls it by user_code, not session_id), so we
 * open one that adopts the typed code (init_login adopt_user_code) and then confirm it — the
 * ST mints and the CLI's poll-by-code releases the key.
 *
 * Retry-safe AND code-correct. We persist the adopted ST session_id keyed to the code it was
 * adopted for, and reuse it on a retry ONLY when the typed code is unchanged:
 * - Same code retry (e.g. transient confirm failure): reuse the session — re-adopting the same
 *   code would 409 on the platform's nx single-claim index and dead-end the user.
 * - Different code (user corrected a mistype): the stored session is bound to the OLD code, so
 *   confirm would mismatch forever. Adopt fresh — a different code is a new claim, so no 409.
 */
export const useAdoptDeviceCodeMutation = () =>
  useMutation({
    mutationKey: [MUTATION_KEYS.ADOPT_DEVICE_CODE],
    mutationFn: async ({ userCode }: AdoptDeviceCodeVariables) => {
      const storedSessionId = getSessionIdFromSessionStorage();
      const storedForCode = ((): string | undefined => {
        try {
          return (
            sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_CODE) ?? undefined
          );
        } catch {
          // Absence is `undefined` (codebase convention), not null.
          return undefined;
        }
      })();

      // Reuse the persisted session only if it was adopted for THIS code; otherwise adopt fresh.
      let sessionId =
        storedSessionId && storedForCode === userCode
          ? storedSessionId
          : undefined;

      if (!sessionId) {
        const { session_id } = await OIDCAuthenticationClient.initLogin({
          trace_id: getTraceIdFromSessionStorage(),
          // Preserve the in-flight client (cli or toolbox); adopt is valid for both.
          init_client: getInitClientFromSessionStorage(),
          adopt_user_code: userCode,
        });

        sessionId = session_id;

        // Persist session_id + the code it's bound to, so a confirm failure is retryable but a
        // corrected code re-adopts instead of confirming against the wrong session.
        try {
          sessionStorage.setItem(SESSION_STORAGE_KEYS.SESSION_ID, sessionId);
          sessionStorage.setItem(SESSION_STORAGE_KEYS.USER_CODE, userCode);
        } catch {
          // Storage unavailable — the retry will just re-adopt; acceptable degradation.
        }
      }

      // The migrated id_token may be expired by the time we land on the ST (the device
      // login can sit on the browser a while). /device/confirm verifies the Bearer, so
      // mint a fresh one first — otherwise the ST rejects it and the CLI polls forever.
      await refreshTokenForDeviceConfirm();

      const result = await OIDCAuthenticationClient.confirmDeviceCode(
        sessionId,
        userCode,
      );

      // This CLI success page is non-persistent (shouldPersistAuthData=false): the token
      // refresh above seeded ACCESS/ID/REFRESH into localStorage purely to carry the confirm
      // Bearer. Clear them ONLY after a confirmed success, so creds don't linger against that
      // intent. Do NOT clear on a failed confirm: the single-use migration cookie is already
      // consumed, so a same-code retry would then go out Bearer-less and dead-end the CLI.
      if (!shouldPersistAuthData()) {
        clearTokens();
      }

      return result;
    },
  });
